import React, { useState, useEffect, useContext } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { AuthContext } from './context';
import type { AuthProviderProps, AuthContextType } from './types';
import type { Database } from '../../lib/database.types';
import { getEffectiveBusinessId } from '../../lib/businessId';
import { clearAuthCache, logCacheState } from '../../lib/authCache';

type BusinessRow = Database['public']['Tables']['businesses']['Row'];

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);

  // האם קיימת רשומה בטבלת האפליקציה
  const [hasAppUser, setHasAppUser] = useState<boolean | null>(null);
  // ערך completed מתוך public.users (0/1)
  const [appUserCompleted, setAppUserCompleted] = useState<0 | 1 | null>(null);
  // פרטי עסק אם קיימים
  const [business, setBusiness] = useState<BusinessRow | null>(null);
  // מצב מנוי העסק
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    is_active: boolean;
    is_expired: boolean;
    cancel_at_period_end: boolean;
  } | null>(null);

  const [loading, setLoading] = useState(true);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);

  // Track last activity for proactive refresh
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  const navigate = useNavigate();
  const location = useLocation();

  const isExternalPage = location.pathname.startsWith('/book/');
  const isDashboardPage = location.pathname === '/dashboard';

  /** Retry session refresh with exponential backoff */
  const refreshSessionWithRetry = async (maxAttempts = 3): Promise<Session | null> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`[SessionGuardian] Refresh attempt ${attempt}/${maxAttempts}`);

        const { data, error } = await supabase.auth.refreshSession();

        if (error) {
          console.error(`[SessionGuardian] Refresh attempt ${attempt} failed:`, error.message);

          // Wait before retry (exponential backoff: 2s, 5s, 10s)
          if (attempt < maxAttempts) {
            const delay = attempt === 1 ? 2000 : attempt === 2 ? 5000 : 10000;
            console.log(`[SessionGuardian] Waiting ${delay}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }

          // All attempts failed
          console.error('[SessionGuardian] All refresh attempts failed, session lost');
          return null;
        }

        if (data.session) {
          console.log('[SessionGuardian] Session refreshed successfully on attempt', attempt);
          setLastActivity(Date.now());
          return data.session;
        }

        // No session but no error - try again
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }

        return null;
      } catch (err) {
        console.error(`[SessionGuardian] Unexpected error on attempt ${attempt}:`, err);
        if (attempt === maxAttempts) {
          return null;
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    return null;
  };

  /** החזרת users.business_id + completed */
  const fetchAppUserRecord = async (uid: string) => {
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('fetchAppUserRecord timeout')), 3000)
      );

      const queryPromise = supabase
        .from('users')
        .select('id, business_id, completed')
        .eq('id', uid)
        .maybeSingle();

      const { data, error } = (await Promise.race([queryPromise, timeoutPromise])) as any;

      if (error) {
        setHasAppUser(null);
        setAppUserCompleted(null);
        return { hasAppUser: null as boolean | null, businessId: null as string | null, completed: null as 0 | 1 | null };
      }

      if (!data) {
        // יצירת שורת users אם חסרה
        try {
          const { data: authUser } = await supabase.auth.getUser();
          if (authUser.user) {
            const fullName =
              authUser.user.user_metadata?.full_name ||
              authUser.user.user_metadata?.name ||
              authUser.user.email?.split('@')[0] ||
              'User';

            const { error: insertError } = await supabase
              .from('users')
              .insert({
                id: uid,
                email: authUser.user.email || '',
                phone: authUser.user.user_metadata?.phone || '',
                role: 'admin',
                completed: 0,
                metadata: {
                  full_name: fullName,
                  avatar_url:
                    authUser.user.user_metadata?.avatar_url || authUser.user.user_metadata?.picture,
                },
              });

            if (!insertError) {
              const { data: retryData } = await supabase
                .from('users')
                .select('id, business_id, completed')
                .eq('id', uid)
                .maybeSingle();

              if (retryData) {
                const completed = (retryData.completed as number | null) ?? 0;
                setHasAppUser(true);
                setAppUserCompleted((completed ? 1 : 0) as 0 | 1);
                return {
                  hasAppUser: true,
                  businessId: (retryData.business_id as string | null) ?? null,
                  completed: (completed ? 1 : 0) as 0 | 1,
                };
              }
            }
          }
        } catch {}

        setHasAppUser(false);
        setAppUserCompleted(null);
        return { hasAppUser: false, businessId: null, completed: null };
      }

      const completed = (data.completed as number | null) ?? 0;
      setHasAppUser(true);
      setAppUserCompleted((completed ? 1 : 0) as 0 | 1);

      return {
        hasAppUser: true,
        businessId: (data.business_id as string | null) ?? null,
        completed: (completed ? 1 : 0) as 0 | 1,
      };
    } catch {
      setHasAppUser(null);
      setAppUserCompleted(null);
      return { hasAppUser: null as boolean | null, businessId: null as string | null, completed: null as 0 | 1 | null };
    }
  };

  /** טוען מצב appUser + עסק עבור המשתמש הנוכחי (ללא שבירת הרשמה) */
  const refreshBusiness = async (currentUser?: User | null) => {
    const effectiveUser = currentUser ?? user;

    if (!effectiveUser?.id) {
      console.log('[AuthProvider] No user, clearing business state');
      setHasAppUser(null);
      setAppUserCompleted(null);
      setBusiness(null);
      setLoading(false);
      return;
    }

    // בזמן הרשמה—דלג כדי לא להתערב בזרימה
    const isRegistrationFlow = window.location.pathname.includes('/register');
    if (isRegistrationFlow) {
      console.log('[AuthProvider] Registration flow detected, skipping business refresh');
      setHasAppUser(true);
      setAppUserCompleted(0);
      setBusiness(null);
      setLoading(false);
      return;
    }

    try {
      console.log('[AuthProvider] Refreshing business for user:', effectiveUser.id);

      const { hasAppUser: appUserFlag, businessId, completed } =
        await fetchAppUserRecord(effectiveUser.id);

      if (appUserFlag === null) {
        console.log('[AuthProvider] Unknown app user state (DB error) — keeping current business');
        setLoading(false);
        return;
      }

      if (appUserFlag === false) {
        console.log('[AuthProvider] No app user found');
        setBusiness(null);
        setLoading(false);
        return;
      }

      console.log('[AuthProvider] App user found, completed:', completed);
      setAppUserCompleted(completed);

      let effectiveBusinessId: string | null = businessId;

      // 💡 Fallback עדין: אם למשתמש אין business_id, ננסה לחלץ מה־DB (subscriptions > users)
      if (!effectiveBusinessId) {
        // 1) נסה מה־subscriptions של המשתמש (האחרון)
        const { data: sub, error: subErr } = await supabase
          .from('subscriptions')
          .select('business_id')
          .eq('user_id', effectiveUser.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!subErr && sub?.business_id) {
          effectiveBusinessId = sub.business_id as string;

          // קבע users.business_id אם עדיין ריק (לא נכשיל UI אם RLS)
          await supabase
            .from('users')
            .update({ business_id: effectiveBusinessId })
            .eq('id', effectiveUser.id)
            .is('business_id', null);
        }
      }

      // 2) אם עדיין חסר—נסה RESOLVE כללי (שיטה אחידה המשמשת גם בפרונט)
      if (!effectiveBusinessId) {
        try {
          effectiveBusinessId = (await getEffectiveBusinessId(effectiveUser.id)) ?? null;
        } catch {}
      }

      if (effectiveBusinessId) {
        console.log('[AuthProvider] Fetching business:', effectiveBusinessId);

        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('*')
          .eq('id', effectiveBusinessId)
          .single();

        if (businessError) {
          console.error(
            '[AuthProvider] Error fetching business:',
            businessError,
            '— keeping current business'
          );
          setLoading(false);
          return;
        }

        setBusiness(businessData);
        console.log('[AuthProvider] Business loaded successfully');

        // טעינת מצב מנוי (אם יש unified view)
        const { data: subData, error: subError } = await supabase
          .from('unified_subscriptions')
          .select('is_active, is_expired, cancel_at_period_end')
          .eq('business_id', effectiveBusinessId)
          .maybeSingle();

        if (subError) {
          setSubscriptionStatus(null);
        } else if (subData) {
          setSubscriptionStatus({
            is_active: subData.is_active ?? false,
            is_expired: subData.is_expired ?? true,
            cancel_at_period_end: subData.cancel_at_period_end ?? false,
          });
        } else {
          setSubscriptionStatus(null);
        }
      } else {
        setBusiness(null);
        setSubscriptionStatus(null);
      }
    } catch {
      setBusiness(null);
    } finally {
      setLoading(false);
    }
  };

  /** אתחול */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);

        if (isExternalPage) {
          setHasAppUser(null);
          setAppUserCompleted(null);
          setBusiness(null);
          setLoading(false);
          return;
        }

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[AuthProvider] Session error:', sessionError);

          // Try to recover session with retry before giving up
          console.log('[AuthProvider] Attempting to recover session...');
          const recoveredSession = await refreshSessionWithRetry(2);

          if (recoveredSession) {
            console.log('[AuthProvider] Session recovered successfully');
            setSession(recoveredSession);
            setUser(recoveredSession.user);
            await refreshBusiness(recoveredSession.user);
            return;
          }

          // Only clear if truly fatal and recovery failed
          const fatal =
            sessionError.message?.includes('refresh_token') ||
            sessionError.message?.includes('Invalid Refresh Token') ||
            // @ts-ignore
            sessionError.status === 400;

          if (fatal) {
            console.error('[AuthProvider] Fatal session error, clearing state');
            logCacheState();
            await supabase.auth.signOut();
            clearAuthCache();
            setSession(null);
            setUser(null);
            setHasAppUser(null);
            setAppUserCompleted(null);
            setBusiness(null);
          }
          setLoading(false);
          return;
        }

        if (session?.user) {
          setSession(session);
          setUser(session.user);
          await refreshBusiness(session.user);
        } else {
          console.log('[AuthProvider] No session found');
          setSession(null);
          setUser(null);
          setHasAppUser(null);
          setAppUserCompleted(null);
          setBusiness(null);
          setLoading(false);
        }
      } catch (error: any) {
        console.error('[AuthProvider] Initialization error:', error);
        setSession(null);
        setUser(null);
        setHasAppUser(null);
        setAppUserCompleted(null);
        setBusiness(null);
        setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthProvider] Auth state changed:', event);

      if (isExternalPage) return;

      if (event === 'SIGNED_OUT') {
        console.log('[AuthProvider] User signed out, clearing state');
        setSession(null);
        setUser(null);
        setHasAppUser(null);
        setAppUserCompleted(null);
        setBusiness(null);
        setLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESHED' && !session) {
        console.warn('[AuthProvider] Token refresh returned no session, attempting manual refresh...');

        // Try to get session again with retry logic
        const recoveredSession = await refreshSessionWithRetry(3);

        if (recoveredSession) {
          console.log('[AuthProvider] Session recovered successfully');
          setSession(recoveredSession);
          setUser(recoveredSession.user);
          await refreshBusiness(recoveredSession.user);
          return;
        }

        // Only logout if all retries failed
        console.error('[AuthProvider] Token refresh failed after all retries, clearing state');
        logCacheState();
        await supabase.auth.signOut();
        clearAuthCache();
        setSession(null);
        setUser(null);
        setHasAppUser(null);
        setAppUserCompleted(null);
        setBusiness(null);
        setLoading(false);
        return;
      }

      if (session?.user) {
        setSession(session);
        setUser(session.user);
        await refreshBusiness(session.user);
      } else {
        setSession(null);
        setUser(null);
        setHasAppUser(null);
        setAppUserCompleted(null);
        setBusiness(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [isExternalPage]); // eslint-disable-line

  /** Session Guardian: Check and refresh session when page returns to focus */
  useEffect(() => {
    if (isExternalPage || !user) return;

    let proactiveRefreshTimer: NodeJS.Timeout;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && user) {
        console.log('[SessionGuardian] Page became visible, checking session...');

        const timeSinceLastActivity = Date.now() - lastActivity;
        const oneHourInMs = 60 * 60 * 1000;

        // If more than 1 hour passed, proactively refresh
        if (timeSinceLastActivity > oneHourInMs) {
          console.log('[SessionGuardian] More than 1 hour passed, refreshing session...');

          const { data } = await supabase.auth.getSession();

          if (!data.session) {
            console.log('[SessionGuardian] No session found, attempting refresh...');
            const refreshedSession = await refreshSessionWithRetry(3);

            if (refreshedSession) {
              console.log('[SessionGuardian] Session refreshed successfully');
              setSession(refreshedSession);
              setUser(refreshedSession.user);
              await refreshBusiness(refreshedSession.user);
            } else {
              console.error('[SessionGuardian] Failed to refresh session after page return');
              toast.error('נדרש להתחבר מחדש');
              await supabase.auth.signOut();
              navigate('/login');
            }
          } else {
            console.log('[SessionGuardian] Session is still valid');
            setLastActivity(Date.now());
          }
        } else {
          console.log('[SessionGuardian] Page returned within 1 hour, session should be fine');
          setLastActivity(Date.now());
        }
      }
    };

    // Proactive Refresh: Check every 5 minutes if token is about to expire
    const startProactiveRefresh = () => {
      proactiveRefreshTimer = setInterval(async () => {
        if (!user) return;

        const { data } = await supabase.auth.getSession();

        if (data.session) {
          // Check if token expires in less than 10 minutes
          const expiresAt = data.session.expires_at;
          if (expiresAt) {
            const expiresIn = expiresAt - Math.floor(Date.now() / 1000);
            const tenMinutesInSeconds = 10 * 60;

            if (expiresIn < tenMinutesInSeconds && expiresIn > 0) {
              console.log('[SessionGuardian] Token expires in', expiresIn, 'seconds, proactively refreshing...');

              const refreshedSession = await refreshSessionWithRetry(2);

              if (refreshedSession) {
                console.log('[SessionGuardian] Proactive refresh successful');
                setSession(refreshedSession);
                setUser(refreshedSession.user);
              } else {
                console.error('[SessionGuardian] Proactive refresh failed');
              }
            }
          }
        }
      }, 5 * 60 * 1000); // Check every 5 minutes
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    startProactiveRefresh();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (proactiveRefreshTimer) {
        clearInterval(proactiveRefreshTimer);
      }
    };
  }, [isExternalPage, user, lastActivity]); // eslint-disable-line

  /** אימייל+סיסמה */
  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      if (data.user) {
        setSession(data.session);
        setUser(data.user);
        setLastActivity(Date.now());
        await refreshBusiness(data.user);
        navigate('/dashboard');
        toast.success('התחברת בהצלחה!');
      }
    } catch (error: any) {
      toast.error(error.message || 'שגיאה בהתחברות');
      throw error;
    }
  };

  /** יציאה */
  const signOut = async () => {
    try {
      console.log('[AuthProvider] Signing out...');
      logCacheState();

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setSession(null);
      setUser(null);
      setHasAppUser(null);
      setAppUserCompleted(null);
      setBusiness(null);

      // נקה את כל ה-cache
      clearAuthCache();

      navigate('/login');
      toast.success('התנתקת בהצלחה');
    } catch (error) {
      console.error('[AuthProvider] SignOut error:', error);

      // גם במקרה של שגיאה, נקה את ה-cache
      clearAuthCache();
      setSession(null);
      setUser(null);
      setHasAppUser(null);
      setAppUserCompleted(null);
      setBusiness(null);

      toast.error('שגיאה בהתנתקות');
    }
  };

  /** עדכון עסק */
  const updateBusiness = async (data: {
    name?: string;
    booking_link?: string;
    settings?: any;
    logo_url?: string | null;
    contact_info?: any;
  }) => {
    if (!user) {
      toast.error('לא נמצא משתמש מחובר');
      return;
    }
    try {
      const businessId = business?.id;
      if (!businessId) return;

      const { error: dbError } = await supabase.from('businesses').update(data).eq('id', businessId);

      if (dbError) throw dbError;
      await refreshBusiness();
      toast.success('העסק עודכן בהצלחה');
    } catch (error: any) {
      toast.error(error.message || 'שגיאה בעדכון העסק');
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const configured = import.meta.env.VITE_AUTH_REDIRECT_URL;
      const fallback =
        typeof window !== 'undefined'
          ? `https://loyalflow.co.il/auth/callback`
          : undefined;
      const redirectTo = configured || fallback;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo, flowType: 'pkce' },
      });
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || 'שגיאה בהתחברות עם Google');
    }
  };

  // ניווט אוטומטי לדשבורד כשנכנסים למסכי אורח, ורק אם הכל מוכן
  useEffect(() => {
    const guestPages = ['/', '/login', '/register'];
    if (
      !loading &&
      !isCreatingAccount &&
      user &&
      hasAppUser === true &&
      business &&
      !isExternalPage &&
      guestPages.includes(location.pathname)
    ) {
      navigate('/dashboard');
    }
  }, [loading, isCreatingAccount, user, hasAppUser, business, isExternalPage, location.pathname, navigate]);

  const value: AuthContextType = {
    session,
    user,
    hasAppUser,
    appUserCompleted,
    business,
    subscriptionStatus,
    signOut,
    signIn,
    signInWithGoogle,
    loading,
    refreshBusiness,
    isCreatingAccount,
    updateUserProfile: async (_data) => {},
    updateBusiness,
    isDashboardPage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** hook נוח */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
